import React, { useMemo } from 'react';
import {
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ComposedChart,
  CartesianGrid,
  Area,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { format, getISOWeek, getISOWeekYear, isValid, startOfWeek, addWeeks } from 'date-fns';
import { useForecast } from '@features/timetravel/hooks/useForecast';
import { ForecastSourceType } from '@domain/forecast/models/Forecast';
import { ForecastGrade } from '@features/timetravel/components/ForecastAnalytics/ForecastGrade';
import { useNavigate } from 'react-router-dom';
import { useGenericDrawer } from '@shared/shell/drawer/useGenericDrawer';
import { useBottomSheet } from '@shared/shell/bottomsheet/useBottomSheet';

interface ForecastPreviewProps {
  productId: string;
  forecastGradeFallback?: string;
  connector?: string;
}

const PREVIEW_WEEKS = 12;

const ForecastPreviewTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: { name: string; value: number; fill: string; payload?: { week: number; year: number; date: string } }[];
  label?: string;
}) => {
  const theme = useTheme();

  if (!active || !payload?.length) return null;

  const dataPoint = payload[0]?.payload;
  const date = dataPoint?.date ? new Date(dataPoint.date + 'T12:00:00') : null;

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={{
        backgroundColor: theme.palette.background.paper,
        p: 2,
        borderRadius: 2,
        boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
      }}
    >
      {date && isValid(date) && (
        <Stack direction="row" gap={1} justifyContent="space-between" spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Week {dataPoint?.week ?? getISOWeek(date)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {format(date, 'MMM d, yyyy')}
          </Typography>
        </Stack>
      )}

      <Divider />

      {payload.map((item) => (
        <Stack key={item.name} direction="row" gap={1} justifyContent="space-between" spacing={1}>
          <Typography variant="body2" color="text.secondary">
            {item.name}
          </Typography>
          {item.value !== undefined &&
            item.value !== null &&
            !isNaN(item.value) && (
              <Typography variant="body2" color={item.fill}>
                {item.value.toFixed(0)}
              </Typography>
            )}
        </Stack>
      ))}
    </Stack>
  );
};

export const ForecastPreview: React.FC<ForecastPreviewProps> = ({
  productId,
  forecastGradeFallback,
  connector = 'bol',
}) => {
  const theme = useTheme();
  const { forecast, isLoading } = useForecast({
    sourceType: ForecastSourceType.PRODUCT,
    source: productId,
    connector,
  });

  const navigate = useNavigate();
  const {closeDrawer} = useGenericDrawer();
  const {closeBottomSheet} = useBottomSheet();

  const onViewInTimeTravel =   () => {
    navigate(`/products/${productId}?tab=sales-timetravel`);
    closeDrawer();
    closeBottomSheet();
  };

  const weekYearToDateString = (year: number, week: number): string => {
    const firstWeekOfYear = startOfWeek(new Date(year, 0, 4), { weekStartsOn: 1 });
    const date = addWeeks(firstWeekOfYear, week - 1);
    return format(date, 'yyyy-MM-dd');
  };

  const chartData = useMemo(() => {
    const weekly = forecast?.weeklyForecasts || [];
    const now = new Date();
    const currentYear = getISOWeekYear(now);
    const currentWeek = getISOWeek(now);

    return weekly
      .filter((w) => w.year > currentYear || (w.year === currentYear && w.week >= currentWeek))
      .sort((a, b) => (a.year === b.year ? a.week - b.week : a.year - b.year))
      .slice(0, PREVIEW_WEEKS)
      .map((w) => ({
        week: w.week,
        year: w.year,
        date: weekYearToDateString(w.year, w.week),
        forecast: w.forecastQuantity ?? 0,
      }));
  }, [forecast?.weeklyForecasts]);

  const grade = forecast?.forecastAnalytics?.grade ?? forecastGradeFallback;

  const { sales4w, sales8w, sales12w } = useMemo(() => {
    const weekly = forecast?.weeklyForecasts || [];
    const now = new Date();
    const currentYear = getISOWeekYear(now);
    const currentWeek = getISOWeek(now);

    const future = weekly
      .filter((w) => w.year > currentYear || (w.year === currentYear && w.week >= currentWeek))
      .sort((a, b) => (a.year === b.year ? a.week - b.week : a.year - b.year));

    const sum = (n: number) =>
      future
        .slice(0, n)
        .reduce((acc, w) => acc + (w.forecastQuantity ?? 0), 0);

    return {
      sales4w: sum(4),
      sales8w: sum(8),
      sales12w: sum(12),
    };
  }, [forecast?.weeklyForecasts]);

  if (isLoading) {
    return (
      <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary">
          Loading forecast...
        </Typography>
      </Stack>
    );
  }

  if (!forecast && !forecastGradeFallback) {
    return (
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          No forecast data available
        </Typography>
        {onViewInTimeTravel && (
          <Button variant="text" color="primary" onClick={onViewInTimeTravel}>
            View in Time Travel
          </Button>
        )}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <ForecastGrade
          grade={grade}
          size="large"
          showLabel={true}
        />
        <Stack direction="row" spacing={2} p={1} sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
          <Stack direction="row" spacing={2}>
            <Typography variant="caption" color="text.secondary">
              Forecast for next:
            </Typography>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            4w: <strong>{Math.round(sales4w)}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            8w: <strong>{Math.round(sales8w)}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            12w: <strong>{Math.round(sales12w)}</strong>
          </Typography>
        </Stack>

        {onViewInTimeTravel && (
          <Button variant="text" color="primary" onClick={onViewInTimeTravel}>
            View in Time Travel
          </Button>
        )}
      </Stack>

      {chartData.length > 0 && (
        <ResponsiveContainer width="100%" height={80}>
          <ComposedChart
            data={chartData}
            margin={{ top: 8, right: 0, bottom: 8, left: 0 }}
          >
            <defs>
              <linearGradient id="forecastPreviewGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor={theme.palette.primary.main} stopOpacity={0.2} />
                <stop offset="1" stopColor={theme.palette.primary.main} stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="0 0" stroke={theme.palette.divider} vertical={false} />
            <YAxis
              interval={1}
              yAxisId="right"
              orientation="right"
              domain={[0, 'auto']}
              fontFamily={theme.typography.fontFamily}
              fontSize={12}
              tickLine={false}
              axisLine={false}
              width={34}
            />
            <Tooltip content={(props: any) => <ForecastPreviewTooltip {...props} />} />
            <Area
              yAxisId="right"
              type="monotone"
              dataKey="forecast"
              name="Forecast"
              stroke={theme.palette.primary.main}
              strokeWidth={3}
              fill="url(#forecastPreviewGradient)"
              dot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}


    </Stack>
  );
};
